import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOMServer from "react-dom/server";
import clsx from "clsx";

import L, {
  DragEndEvent,
  LatLng,
  LatLngLiteral,
  LeafletMouseEvent,
  MapOptions,
  Marker,
  MarkerOptions,
} from "leaflet";

// eslint-disable-next-line import/no-unresolved
import "leaflet.gridlayer.googlemutant";

import { HvInput } from "@hitachivantara/uikit-react-core";

import { IconData, useMapSettings } from "lib/api";

import useStyles, { googleMapStyles } from "./styles";
import Icon from "../Icon";

let isFirstRender = true;

const validateCoordinates = (
  coordinates?: LatLngLiteral
): coordinates is LatLngLiteral =>
  coordinates != null &&
  coordinates.lat >= -90 &&
  coordinates.lat <= 90 &&
  coordinates.lng >= -180 &&
  coordinates.lng <= 180;

const getMarkerIcon = (
  icon: MarkerIcon | undefined,
  classes: ReturnType<typeof useStyles>
) => {
  const color = icon?.color ?? "#16a085";
  const shape = icon?.shape ?? "box";

  const myHtml = ReactDOMServer.renderToString(
    <>
      <div
        className={clsx(classes.marker, classes.markerShadow, {
          [classes.markerCircle]: shape === "circle",
          [classes.markerBox]: shape === "box",
        })}
        style={{ backgroundColor: color }}
      >
        {icon && <Icon iconData={icon} color="#FFFFFF" />}
      </div>
      {shape === "box" && (
        <div
          className={clsx(classes.markerPointer)}
          style={{ borderTopColor: color }}
        />
      )}
    </>
  );

  return L.divIcon({
    className: classes.markerContainer,
    html: myHtml,
    iconSize: [40, 46],
    iconAnchor: [20, 46],
  });
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface MarkerIcon extends IconData {
  shape?: "circle" | "box";
}

export interface MapMarker extends Omit<MarkerOptions, "icon"> {
  key?: string;
  location: LatLngLiteral;
  icon?: MarkerIcon;
}

interface GenericMapProps {
  className?: string;
  onChange?: (location: LatLng) => void;
  onSearch?: (search: string) => void;
  clickToMoveMarker?: boolean;
  dragToMoveMarker?: boolean;
  marker?: MarkerIcon;
  markerOptions?: MarkerOptions;
  location?: LatLngLiteral;
  markers?: MapMarker[];
  searchPlaceholder?: string;
  searchButtonLabel?: string;
  clearButtonLabel?: string;
  disableSearchBox?: boolean;
  showMarkerAtInicialCenter?: boolean;
  mapOptions?: MapOptions;
  mapRef?:
    | React.MutableRefObject<L.Map | undefined>
    | React.RefCallback<L.Map | undefined>;
}

const GenericMap: React.FC<GenericMapProps> = ({
  className,
  onChange,
  onSearch,
  clickToMoveMarker = false,
  dragToMoveMarker = false,
  marker,
  markerOptions,
  location,
  markers,
  searchPlaceholder,
  searchButtonLabel,
  clearButtonLabel,
  disableSearchBox = false,
  showMarkerAtInicialCenter = false,
  mapOptions,
  mapRef,
}) => {
  const classes = useStyles();
  const isValidLocation = validateCoordinates(location);

  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<L.Map>();
  const [map, setMap] = useState<L.Map>();

  const [locationMapMarker, setLocationMapMarker] = useState<Marker | null>(
    null
  );
  const [centerMapMarker, setCenterMapMarker] = useState<Marker | null>(null);
  const [markersLayer, setMarkersLayer] = useState<L.FeatureGroup | null>(null);

  const mapSettings = useMapSettings();

  const hasValidLocation = useRef(isValidLocation);

  const initialCenter = useRef<LatLng>(
    isValidLocation && location != null
      ? new LatLng(location.lat, location.lng)
      : new LatLng(mapSettings.lat, mapSettings.lng)
  );
  const initialZoom = useRef<number>(mapSettings.zoom);

  const markerIcon = useMemo(
    () => getMarkerIcon(marker, classes),
    [classes, marker]
  );

  useEffect(() => {
    hasValidLocation.current = isValidLocation;
  }, [isValidLocation]);

  // #region Map management
  // creation
  useEffect(() => {
    if (mapInstance.current == null && mapContainer.current != null) {
      const baseLayer = L.gridLayer.googleMutant({
        type: "roadmap",
        styles: googleMapStyles,
        maxZoom: 21,
        ...{ attributionControl: false },
      });

      mapInstance.current = L.map(mapContainer.current, {
        minZoom: 2,
        maxZoom: 21,
        attributionControl: false,
        worldCopyJump: true,

        center: initialCenter.current,
        zoom: initialZoom.current,

        layers: [baseLayer],

        ...mapOptions,
        zoomControl:
          mapOptions?.zoomControl === true ? false : mapOptions?.zoomControl,
      });
      if (
        mapOptions?.zoomControl === true ||
        mapOptions?.zoomControl === undefined
      ) {
        mapInstance.current.addControl(
          L.control.zoom({
            position: "bottomright",
            zoomInText: "",
            zoomOutText: "",
          })
        );
      }
    }
  }, [mapOptions]);

  // map settings update
  useEffect(() => {
    if (mapInstance.current != null) {
      const differentLocation = !initialCenter.current.equals({
        lat: mapSettings.lat,
        lng: mapSettings.lng,
      });
      const differentZoom = initialZoom.current !== mapSettings.zoom;

      if (differentLocation && !hasValidLocation.current) {
        if (showMarkerAtInicialCenter) {
          if (differentZoom) {
            mapInstance.current.setZoom(mapSettings.zoom, {
              animate: false,
            });
          }
        } else {
          mapInstance.current.setView(
            {
              lat: mapSettings.lat,
              lng: mapSettings.lng,
            },
            mapSettings.zoom
          );
        }
      } else if (differentZoom) {
        mapInstance.current.setZoom(mapSettings.zoom, {
          animate: false,
        });
      }
    }
  }, [
    mapSettings.lat,
    mapSettings.lng,
    mapSettings.zoom,
    showMarkerAtInicialCenter,
  ]);

  // listeners
  useEffect(() => {
    let onMapClick: (event: LeafletMouseEvent) => void;

    if (map != null) {
      if (clickToMoveMarker) {
        onMapClick = (event) => {
          onChange?.(event.latlng);
        };

        map.on("click", onMapClick);
      }
    }

    return () => {
      if (map != null && onMapClick != null) {
        map.off("click", onMapClick);
      }
    };
  }, [map, onChange, clickToMoveMarker]);
  // #endregion

  // #region Location marker management
  // creation
  useEffect(() => {
    if (map != null) {
      if (validateCoordinates(location)) {
        if (locationMapMarker == null) {
          const lm = L.marker(location, {
            icon: markerIcon,
            draggable: dragToMoveMarker,
            autoPan: dragToMoveMarker,
            keyboard: false,
            zIndexOffset: 100,
            riseOnHover: true,
            riseOffset: 200,
            ...markerOptions,
          });
          lm.addTo(map);

          setLocationMapMarker(lm);
        }
      } else if (locationMapMarker != null) {
        map.removeLayer(locationMapMarker);

        setLocationMapMarker(null);
      }
    } else if (locationMapMarker != null) {
      setLocationMapMarker(null);
    }
  }, [
    map,
    locationMapMarker,
    location,
    markerIcon,
    dragToMoveMarker,
    markerOptions,
  ]);

  // update
  useEffect(() => {
    if (map != null) {
      if (validateCoordinates(location)) {
        if (locationMapMarker != null) {
          if (!locationMapMarker.getLatLng().equals(location)) {
            locationMapMarker.setLatLng(location);
            // map.panTo(locationMapMarker.getLatLng());
          }
          if (locationMapMarker.getIcon() !== markerIcon) {
            locationMapMarker.setIcon(markerIcon);
          }
        }
      }
    }
  }, [map, locationMapMarker, location, markerIcon]);

  // listeners
  useEffect(() => {
    let onMarkerDrag: (event: DragEndEvent) => void;

    if (locationMapMarker != null) {
      if (dragToMoveMarker) {
        onMarkerDrag = (event) => {
          onChange?.(event.target.getLatLng());
        };

        locationMapMarker.on("dragend", onMarkerDrag);
      }
    }

    return () => {
      if (locationMapMarker != null && onMarkerDrag != null) {
        locationMapMarker.off("dragend", onMarkerDrag);
      }
    };
  }, [locationMapMarker, onChange, dragToMoveMarker]);
  // #endregion

  // #region Center marker management
  // creation
  useEffect(() => {
    if (map != null) {
      if (showMarkerAtInicialCenter) {
        if (centerMapMarker == null) {
          const cm = L.marker(mapSettings, {
            icon: markerIcon,
            draggable: false,
            keyboard: false,
            interactive: false,
            ...markerOptions,
          });
          cm.addTo(map);
          map.panTo(cm.getLatLng(), {
            animate: false,
          });

          setCenterMapMarker(cm);
        }
      } else if (centerMapMarker != null) {
        map.removeLayer(centerMapMarker);

        setCenterMapMarker(null);
      }
    } else if (centerMapMarker != null) {
      setCenterMapMarker(null);
    }
  }, [
    map,
    centerMapMarker,
    mapSettings,
    markerIcon,
    showMarkerAtInicialCenter,
    markerOptions,
  ]);

  // update;
  useEffect(() => {
    if (map != null) {
      if (centerMapMarker != null) {
        if (!centerMapMarker.getLatLng().equals(mapSettings)) {
          centerMapMarker.setLatLng(mapSettings);
          map.panTo(centerMapMarker.getLatLng());
        }
        if (centerMapMarker.getIcon() !== markerIcon) {
          centerMapMarker.setIcon(markerIcon);
        }
      }
    }
  }, [map, mapSettings, markerIcon, centerMapMarker]);
  // #endregion

  // #region Other markers management
  const markersIndex = useRef<Record<string | number | symbol, Marker>>({});
  const markersWithoutKey = useRef<Array<Marker>>([]);

  const hasMarkers = markers != null && markers.length > 0;

  // layer creation
  useEffect(() => {
    if (map != null) {
      if (hasMarkers && markersLayer == null) {
        const ml = L.featureGroup();
        ml.addTo(map);

        setMarkersLayer(ml);
      } else if (!hasMarkers && markersLayer != null) {
        map.removeLayer(markersLayer);

        setMarkersLayer(null);
      }
    } else {
      // clean everything
      markersWithoutKey.current.splice(0, markersWithoutKey.current.length);
      Object.keys(markersIndex.current).forEach((key) => {
        delete markersIndex.current[key];
      });

      if (markersLayer != null) {
        setMarkersLayer(null);
      }
    }
  }, [map, markersLayer, hasMarkers]);

  // markers creation and updates
  useEffect(() => {
    if (map != null && markersLayer != null) {
      const foundKeys: Record<string | number | symbol, boolean> = {};

      // remove markers without keys
      markersWithoutKey.current
        .splice(0, markersWithoutKey.current.length)
        .forEach((m) => {
          markersLayer.removeLayer(m);
        });

      if (markers != null && markers.length > 0) {
        markers.forEach((mapMarker) => {
          const {
            key,
            location: markerLocation,
            icon,
            ...otherOptions
          } = mapMarker;

          let m =
            key != null && markersIndex.current[key] != null
              ? markersIndex.current[key]
              : null;

          if (m == null) {
            // creation
            m = L.marker(markerLocation, {
              icon: getMarkerIcon(icon, classes),
              keyboard: false,
              zIndexOffset: 0,
              riseOnHover: true,
              riseOffset: 200,
              ...otherOptions,
            });
            m.addTo(markersLayer);

            if (key != null) {
              markersIndex.current[key] = m;
              foundKeys[key] = true;
            } else {
              markersWithoutKey.current.push(m);
            }
          } else {
            // update
            // eslint-disable-next-line no-lonely-if
            if (!m.getLatLng().equals(markerLocation)) {
              m.setLatLng(markerLocation);
            }
            // TODO not sure how to know if icon changed, and building one
            // is still a bit intensive (it calls ReactDOMServer.renderToString)
            // this isn't needed for now, so not implemented
            // if (m.getIcon() !== icon) {
            //   m.setIcon(getMarkerIcon(icon, classes));
            // }
          }
        });
      }

      // remove missing keys
      Object.keys(markersIndex.current).forEach((key) => {
        if (foundKeys[key] !== true) {
          markersLayer.removeLayer(markersIndex.current[key]);
          delete markersIndex.current[key];
        }
      });
    }
  }, [map, markersLayer, markers, classes]);
  // #endregion

  // #region Async map operations
  const firstMapViewUpdate = useRef(true);
  const updateMapView = useCallback(
    async function updateMapView() {
      if (map != null) {
        if (locationMapMarker != null || markersLayer != null) {
          await sleep(500);
          if (locationMapMarker != null && markersLayer != null) {
            map.setView(
              locationMapMarker.getLatLng(),
              Math.min(
                map.getBoundsZoom(markersLayer.getBounds()),
                map.getZoom()
              ),
              {
                animate: !firstMapViewUpdate.current,
              }
            );
          } else if (markersLayer != null) {
            map.fitBounds(markersLayer.getBounds(), {
              animate: !firstMapViewUpdate.current,
            });
          } else if (locationMapMarker != null) {
            map.panTo(locationMapMarker.getLatLng(), {
              animate: !firstMapViewUpdate.current,
            });
          }
        }

        firstMapViewUpdate.current = false;
      }
    },
    [map, markersLayer, locationMapMarker]
  );

  // center map on location or fit bounds to show other markers
  useEffect(() => {
    updateMapView();
  }, [updateMapView, location, markers]);

  const invalidateSize = useCallback(async function invalidateSize() {
    if (mapInstance.current != null) {
      await sleep(isFirstRender ? 2500 : 750);
      if (isFirstRender) isFirstRender = false;
      mapInstance.current.invalidateSize();
      setMap(mapInstance.current);
    }
  }, []);

  // recalculate map layout and mark it as ready
  useEffect(() => {
    invalidateSize();
  }, [invalidateSize]);
  // #endregion

  useEffect(() => {
    if (mapRef != null) {
      if ("current" in mapRef) {
        // eslint-disable-next-line no-param-reassign
        mapRef.current = map;
      } else {
        mapRef(map);
      }
    }
  }, [map, mapRef]);

  const handleSearch = async (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement>,
    val: string
  ) => {
    event.preventDefault();
    await onSearch?.(val);
  };

  return (
    <div className={clsx(classes.root, className)}>
      {!disableSearchBox && (
        <HvInput
          className={classes.searchBox}
          type="search"
          aria-label={searchButtonLabel}
          placeholder={searchPlaceholder}
          labels={{
            clearButtonLabel,
          }}
          onEnter={handleSearch}
        />
      )}
      <div ref={mapContainer} className={classes.mapContainer} />
    </div>
  );
};

const MemoGenericMap = React.memo(GenericMap, (prevProps, nextProps) => {
  // trying to re-render only when really needed to avoid google maps rendering issues
  // (calling invalidateSize doesn't seem to always solve the problem)
  // we consider all properties to be static except location, marker and markers,
  // and the onChange and onSearch callbacks because it can depend on the parent's clousure.
  // those should receive care to be stable, via useMemo, useCallback, etc..
  return (
    prevProps != null &&
    nextProps != null &&
    prevProps.location === nextProps.location &&
    prevProps.marker === nextProps.marker &&
    prevProps.markers === nextProps.markers &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onSearch === nextProps.onSearch
  );
});

export default MemoGenericMap;
